import { memo, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { date, dialog, dom } from 'app/utils';
import { Breadcrumb } from 'app/components';
import { Button, Icon, MenuButton } from 'app/components/common';
import { Form } from 'app/components/layout';
import { DateBox, Field, Select, TextArea, TextBox } from 'app/components/form';
import { resources, profiles } from 'app/services';
import { IMedia, IMembership, IProfile, IStamp } from 'app/models';
import Upgrade from './upgrade';

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const controller = useFormik<IProfile>({
    initialValues: (useLoaderData() ?? {}) as IProfile,
    validationSchema: useMemo(() => yup.object({
      firstName: yup.string().min(2).max(50).required(),
      lastName: yup.string().min(2).max(50).required(),
      birthDate: yup.date().required(),
      country: yup.string().required(),
      nationality: yup.string().required(),
      professionId: yup.string().required(),
      experience: yup.number().oneOf([1, 2, 3, 4, 5]).required(),
      description: yup.string().required(),
      status: yup.number().oneOf([0, 1, 2]).required(),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;

  const onSave = () => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    profiles.save(controller.values).then(({ success, data }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('profiles.save.success') });
      navigate(`../${data.id}`);
    });
  };

  const onUpgrade = (stamp: IStamp) => dialog.show({ key: 'profile.upgrade', closable: false,
    header: t('upgrade.title'),
    template: (<Upgrade data={{
      status: 1,
      profileId: values.id,
      subscriptionId: stamp.id,
      timestamp: stamp.timestamp,
      expiry: date.from(stamp.expiry),
    } as IMembership} onDone={(membership) => {
      dialog.hide('profile.upgrade');
      if (membership) navigate(`../${values.id}`, { replace: true });
    }} />),
  });

  const onRemove = (media: IMedia) => {
    dialog.confirm({ message: t('profiles.medias.delete.question'),
      accept: { label: t('app.delete'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) resources.delete(media as any).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`profiles.medias.delete.success`) });
      });
    });
  };

  const { touched, errors, values } = controller;
  const getProps = useCallback((field: keyof IProfile): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field] && errors[field]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('p-4', 'block', 'flex-1'),

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  const weight = useMemo(() => (values.type === 2 ? 'scale' : 'experience'), [values.type]);

  return (
    <div className="h-full flex flex-column align-items-stretch">
      <div className="flex-none flex surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text severity="warning" href=".." icon="pi-times" label={t('app.cancel')} className="mr-2 px-3 py-1" />
          <Button icon="mdi-check" label={t('app.save')} className="px-3 py-1" onClick={onSave} />
        </div>
      </div>
      <div className="flex flex-auto overflow-auto">
        <form { ...form }>
          <Form>
            <Field text={t('profiles.fields.type')} error={controller.errors.type} align="end" asterisk>
              <Select { ...getProps('type') } options={[
                { label: t('app.profile.employee'), value: 1 },
                { label: t('app.profile.employer'), value: 2 },
              ]} disabled={!!values.id} />
            </Field>
            <Field text={t('profiles.fields.firstName')} error={controller.errors.firstName} align="end" asterisk className="pt-4">
              <TextBox { ...getProps('firstName') } />
            </Field>
            <Field text={t('profiles.fields.lastName')} error={controller.errors.lastName} align="end" asterisk className="pt-4">
              <TextBox { ...getProps('lastName') } />
            </Field>
            { values.type === 2 && <Field text={t('profiles.fields.title')} error={controller.errors.title} align="end" asterisk className="pt-4">
              <TextBox { ...getProps('title') } />
            </Field> }
            <Field text={t(`profiles.fields.${values.type === 2 ? 'foundationDate' : 'birthDate'}`)} error={controller.errors.birthDate} align="end" asterisk className="pt-4">
              <DateBox { ...getProps('birthDate') } />
            </Field>
            { values.type === 1 && <Field text={t('profiles.fields.nationality')} error={controller.errors.nationalityId} align="end" asterisk className="pt-4">
              <Select { ...getProps('nationalityId') } source={{ type: 'lookup', key: 'countries' }} />
            </Field> }
            <Field text={t('profiles.fields.country')} error={controller.errors.countryId} align="end" asterisk className="pt-4">
              <Select { ...getProps('countryId') } source={{ type: 'lookup', key: 'countries' }} filterable />
            </Field>
            <Field text={t('profiles.fields.profession')} error={controller.errors.professionId} align="end" asterisk className="pt-4">
              <Select { ...getProps('professionId') } source={{ type: 'lookup', key: 'professions', filter: (o) => (o.type === values.type) }} filterable />
            </Field>
            <Field text={t(`profiles.fields.${weight}`)} error={controller.errors.experience} align="end" asterisk className="pt-4">
              <Select { ...getProps('experience') } options={[
                { label: t(`app.${weight}.beginner`), value: 1 },
                { label: t(`app.${weight}.intermediate`), value: 2 },
                { label: t(`app.${weight}.advanced`), value: 3 },
                { label: t(`app.${weight}.expert`), value: 4 },
                { label: t(`app.${weight}.master`), value: 5 },
              ]} />
            </Field>
            <Field text={t('profiles.fields.description')} error={controller.errors.description} align="end" asterisk className="pt-4">
              <TextArea { ...getProps('description') } style={{ height: '6rem' }} />
            </Field>
            <Field text={t('profiles.fields.status')} error={controller.errors.status} align="end" asterisk className="pt-4">
              <Select { ...getProps('status') } options={[
                { label: t('app.status.disabled'), value: 0 },
                { label: t('app.status.enabled'), value: 1 },
                { label: t('app.status.approved'), value: 2 },
              ]} />
            </Field>
          </Form>
        </form>
        <div className="p-2 flex flex-column align-items-stretch flex-1">
          { values.type === 1 && <div className="m-3 mb-2 flex align-items-center border-round bg-primary text-white" style={{ minHeight: '3rem' }}>
            <Icon name="mdi-verified" className="ml-3" />
            <div className="px-3 flex-1">{ values.stamp?.name ?? '' }</div>
            <Button text rounded icon="mdi-cached" className="text-white" onClick={() => { onUpgrade(values.stamp!) }} />
          </div> }
          <div className="flex flex-wrap align-items-start">
            { values.medias?.map((media) => (
              <div key={media.id} className="m-3 relative border-round surface-200" style={{
                minWidth: '12rem',
                minHeight: '12rem',
                background: `center no-repeat url(${[3].includes(media.type) ? media.cover : media.url})`,
                backgroundSize: 'contain',
              }}>
                <MenuButton text icon="mdi-more-horiz" rounded alignment="right" items={[
                  { label: t('app.delete'), icon: (<Icon name="mdi-delete" className="mr-4" />), command: () => { onRemove(media); } },
                ]} className="absolute right-0 bottom-0" />
              </div>
            )) }
          </div>
        </div>
      </div>
    </div>
  );
});
